import { useEffect, useState } from 'react';
import { apiCall, default as API_CONFIG } from '../config/api';

/**
 * Fetches `/api/tickers/{symbol}/prices?range=...` and exposes the
 * resolved PriceHistory plus loading / error / unavailable states.
 *
 * `unavailable` (404 from backend) is distinct from `error` (network /
 * 5xx). Frontend hides the chart on unavailable, shows a retry on error.
 */
export const usePriceHistory = (symbol, range = 'month') => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    if (!symbol) {
      setData(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setUnavailable(false);

    apiCall(API_CONFIG.endpoints.tickerPrices(symbol, range))
      .then((resp) => {
        if (cancelled) return;
        setData(resp);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = (err?.message || '').toLowerCase();
        if (msg.includes('not found') || msg.includes('404')) {
          setUnavailable(true);
        } else {
          console.error('Failed to load price history', symbol, range, err);
          setError('Failed to load price history');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [symbol, range]);

  return { data, loading, error, unavailable };
};
